import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, RotateCcw, Search, Upload, type LucideIcon } from "@fg/icons";
import type { DriveItem } from "@/api/driveApi";
import { Button } from "@/components/ui/button";
import { Menu, type MenuAnchor } from "@/components/ui/menu";
import { TYPE_FILTERS, matchesFilter, type TypeFilter } from "@/lib/fileTypes";
import { isBoolean, readStored, usePersistentState, writeStored } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { useDriveActions } from "./driveActions";
import { useUploads } from "./upload/UploadProvider";
import { ItemBrowser, type ItemBrowserHandle } from "./items/ItemBrowser";
import { useSelection } from "./items/useSelection";
import { defaultSort, isSortState, sortItems, type SortState } from "./items/sorting";
import { DetailsToggle, FilterChips, SortButton, ViewToggle } from "./items/ViewControls";
import { buildCreateMenu, buildItemMenu } from "./items/menuItems";
import { isFileDrag } from "./items/dnd";
import { CommandBar } from "./CommandBar";
import { DetailsPane, type DetailsTab } from "./details/DetailsPane";
import { useOpenCommentCount } from "./comments/CommentsPanel";
import { EmptyState } from "./EmptyState";
import { isViewMode, type DriveVariant, type ViewMode } from "./types";

/** The subset of a TanStack query result DriveView needs. */
export interface ListQuery {
    data?: DriveItem[];
    isPending: boolean;
    isError: boolean;
    error: Error | null;
    refetch: () => unknown;
}

export interface DriveViewProps {
    variant: DriveVariant;
    /** Accessible name of the item list. */
    label: string;
    title: ReactNode;
    headerExtra?: ReactNode;
    banner?: ReactNode;
    /** Shown above the list while no type filter is on (My Drive's Suggested row). */
    aboveList?: ReactNode;
    query: ListQuery;
    /** Selection resets whenever this changes (folder / page). */
    resetKey: string;
    /** Folder pages: where drops, uploads and "New folder" land. */
    parentId?: string | null;
    currentFolder?: DriveItem | null;
    emptyState: ReactNode;
    /** Select (and scroll to) this item once loaded — `?select=<id>`. */
    selectId?: string | null;
    /** Also preview it (search → file). */
    openSelected?: boolean;
    onSelectHandled?: () => void;
    /** Bump to open the details pane for the current folder. */
    detailsNonce?: number;
}

/**
 * Shared page body for My Drive, Recent, Starred, Shared, Bin and Search:
 * command bar, then (on the canvas) the files card — title, type chips,
 * list/grid browser — and the details card; context menus and
 * drop-to-upload.
 */
export function DriveView(props: DriveViewProps) {
    const { variant, query, resetKey, parentId, currentFolder } = props;
    const actions = useDriveActions();
    const uploads = useUploads();

    const [view, setView] = usePersistentState<ViewMode>("drive.view", "list", isViewMode);
    const [sort, setSort] = usePersistentState<SortState>(`drive.sort.${variant}`, defaultSort(variant), isSortState);
    // Remembered on desktop; never auto-opens as an overlay on small screens.
    const [detailsOpen, setDetailsOpen] = useState(
        () => readStored("drive.details", false, isBoolean) && window.matchMedia("(min-width: 1024px)").matches
    );
    const [filter, setFilter] = useState<TypeFilter | null>(null);
    const [menu, setMenu] = useState<{ anchor: MenuAnchor; targets: DriveItem[]; keyboard: boolean } | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragDepth = useRef(0);
    const browserRef = useRef<ItemBrowserHandle>(null);

    const toggleDetails = (open: boolean) => {
        setDetailsOpen(open);
        writeStored("drive.details", open);
    };

    const data = query.data;
    const visible = useMemo(() => sortItems((data ?? []).filter((i) => matchesFilter(i, filter)), sort), [data, filter, sort]);
    const ids = useMemo(() => visible.map((i) => i.id), [visible]);
    const selection = useSelection(ids, resetKey);
    const selectedItems = useMemo(() => visible.filter((i) => selection.selected.has(i.id)), [visible, selection.selected]);

    const zipParentFor = (items: DriveItem[]) => (variant === "folder" ? parentId ?? null : items[0]?.parentId ?? null);
    const detailsItem = selectedItems.length === 1 ? selectedItems[0]! : selectedItems.length === 0 ? currentFolder ?? null : null;

    // Comments: the item menu's "Comment" opens the pane on its Comments tab;
    // the details toggle shows the selected file's open-thread count.
    const qc = useQueryClient();
    const [detailsTabRequest, setDetailsTabRequest] = useState<{ tab: DetailsTab; nonce: number } | null>(null);
    const single = selectedItems.length === 1 ? selectedItems[0]! : null;
    const badgeItem = single && single.kind === "File" && variant !== "trash" ? single : null;
    const commentCount = useOpenCommentCount(badgeItem?.id ?? null);
    const openComments = (item: DriveItem) => {
        selection.selectOnly(item.id);
        toggleDetails(true);
        setDetailsTabRequest((r) => ({ tab: "comments", nonce: (r?.nonce ?? 0) + 1 }));
    };
    const openDetails = (item: DriveItem) => {
        selection.selectOnly(item.id);
        toggleDetails(true);
    };

    // ?select=<id>: select, scroll to and (optionally) preview an item.
    const handledSelect = useRef<string | null>(null);
    useEffect(() => {
        const id = props.selectId;
        if (!id) {
            handledSelect.current = null;
            return;
        }
        if (!data || handledSelect.current === id) return;
        const item = data.find((i) => i.id === id);
        // Not in the list yet (e.g. a just-finished upload before the refetch
        // lands) — keep the param and try again when the data changes.
        if (!item) return;
        handledSelect.current = id;
        if (!matchesFilter(item, filter)) setFilter(null);
        window.requestAnimationFrame(() => selection.selectOnly(item.id, { focus: true }));
        if (props.openSelected && item.kind === "File") actions.preview(item, sortItems(data, sort));
        props.onSelectHandled?.();
        // Runs when the target or data arrives; other values are read fresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectId, data]);

    useEffect(() => {
        if (!props.detailsNonce) return;
        selection.clear();
        toggleDetails(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.detailsNonce]);

    // When focus is on the page itself (nothing focused yet, or after a
    // dialog closed), still let list shortcuts work.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented) return;
            const a = document.activeElement;
            const idle = !a || a === document.body || a.id === "main-content";
            if (!idle || document.querySelector("[data-drive-overlay]")) return;
            browserRef.current?.handleKey(e);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // A drop anywhere (including on a folder row that stops propagation)
    // or a cancelled drag always clears the page overlay.
    useEffect(() => {
        const reset = () => {
            dragDepth.current = 0;
            setDragging(false);
        };
        window.addEventListener("drop", reset, true);
        window.addEventListener("dragend", reset, true);
        return () => {
            window.removeEventListener("drop", reset, true);
            window.removeEventListener("dragend", reset, true);
        };
    }, []);

    const openItemMenu = (item: DriveItem, anchor: MenuAnchor, keyboard: boolean) => {
        const inSel = selection.has(item.id);
        if (!inSel) selection.selectOnly(item.id);
        setMenu({ anchor, targets: inSel ? selectedItems : [item], keyboard });
    };

    const commitRename = (item: DriveItem, name: string | null) => {
        setRenamingId(null);
        selection.setFocus(item.id, { focus: true });
        const v = name?.trim();
        if (v && v !== item.name) void actions.renameTo(item, v);
    };

    const byIds = (list: string[]) => {
        const set = new Set(list);
        return (data ?? []).filter((i) => set.has(i.id));
    };

    const canDropUpload = variant === "folder";
    const rootDrag = canDropUpload
        ? {
              onDragEnter: (e: DragEvent<HTMLDivElement>) => {
                  if (!isFileDrag(e)) return;
                  dragDepth.current += 1;
                  setDragging(true);
              },
              onDragLeave: (e: DragEvent<HTMLDivElement>) => {
                  if (!isFileDrag(e)) return;
                  dragDepth.current = Math.max(0, dragDepth.current - 1);
                  if (dragDepth.current === 0) setDragging(false);
              },
              onDragOver: (e: DragEvent<HTMLDivElement>) => {
                  if (!isFileDrag(e)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
              },
              onDrop: (e: DragEvent<HTMLDivElement>) => {
                  if (!isFileDrag(e)) return;
                  e.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  uploads.uploadDataTransfer(e.dataTransfer, parentId ?? null);
              }
          }
        : {};

    let content: ReactNode;
    if (query.isPending) {
        content = <BrowserSkeleton view={view} />;
    } else if (query.isError) {
        content = (
            <EmptyState icon={CircleAlert} tone="rose" title="Couldn't load this list" description={query.error?.message}>
                <Button variant="outline" onClick={() => void query.refetch()}>
                    <RotateCcw className="h-4 w-4" /> Try again
                </Button>
            </EmptyState>
        );
    } else if (visible.length === 0) {
        const filterLabel = TYPE_FILTERS.find((f) => f.id === filter)?.label.toLowerCase();
        content =
            filter && (data?.length ?? 0) > 0 ? (
                <EmptyState icon={Search} tone="slate" title={`No ${filterLabel ?? "matching items"} here`}>
                    <Button variant="outline" onClick={() => setFilter(null)}>Clear filter</Button>
                </EmptyState>
            ) : (
                props.emptyState
            );
    } else {
        content = (
            <ItemBrowser
                ref={browserRef}
                items={visible}
                view={view}
                variant={variant}
                selection={selection}
                sort={sort}
                onSortChange={setSort}
                renamingId={renamingId}
                onRenameStart={(item) => setRenamingId(item.id)}
                onRenameCommit={commitRename}
                onOpen={(item) => actions.open(item, visible)}
                onPreview={(item) => actions.preview(item, visible)}
                onDelete={(items) => void (variant === "trash" ? actions.deleteForever(items) : actions.trash(items))}
                onContextMenu={openItemMenu}
                onMoveDrop={(movedIds, target) => void actions.moveTo(byIds(movedIds), target)}
                onFilesDrop={(dt, folderId) => uploads.uploadDataTransfer(dt, folderId)}
                label={props.label}
            />
        );
    }

    return (
        <div className="relative flex min-h-0 flex-1 flex-col bg-[var(--drive-canvas)]" {...rootDrag}>
            <CommandBar
                items={selectedItems}
                variant={variant}
                list={visible}
                zipParentId={zipParentFor(selectedItems)}
                onRename={(item) => {
                    selection.selectOnly(item.id);
                    setRenamingId(item.id);
                }}
                onDetails={openDetails}
                onComment={openComments}
                onClear={selection.clear}
                trailing={
                    <>
                        <SortButton variant={variant} sort={sort} onChange={setSort} />
                        <span className="w-1 shrink-0" aria-hidden />
                        <ViewToggle view={view} onChange={setView} />
                        <span className="w-1 shrink-0" aria-hidden />
                        <DetailsToggle open={detailsOpen} onToggle={() => toggleDetails(!detailsOpen)} badge={commentCount.count} />
                    </>
                }
            />
            <div className="flex min-h-0 flex-1 gap-2 p-1.5 sm:p-2">
                <section
                    aria-label="Files"
                    className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-card text-card-foreground shadow-[var(--drive-shadow-4)]"
                >
                    <div className="shrink-0 px-4 pt-3">
                        <div className="flex min-h-9 items-center gap-2">
                            <div className="min-w-0 flex-1">{props.title}</div>
                            {props.headerExtra}
                        </div>
                        {props.banner ? <div className="pt-1.5">{props.banner}</div> : null}
                        <FilterChips value={filter} onChange={setFilter} />
                    </div>
                    <div
                        className="min-h-0 flex-1 overflow-auto pb-24"
                        onClick={(e) => {
                            if (!(e.target as Element).closest("[data-item-id],[data-no-clear]")) selection.clear();
                        }}
                        onContextMenu={(e) => {
                            if ((e.target as Element).closest("[data-item-id],[data-no-clear]")) return;
                            if (variant !== "folder") return;
                            e.preventDefault();
                            selection.clear();
                            setMenu({ anchor: { x: e.clientX, y: e.clientY }, targets: [], keyboard: false });
                        }}
                    >
                        {!filter ? props.aboveList : null}
                        {content}
                    </div>
                </section>

                {detailsOpen ? (
                    <DetailsPane
                        item={detailsItem}
                        selectionCount={selectedItems.length}
                        variant={variant}
                        onClose={() => toggleDetails(false)}
                        requestedTab={detailsTabRequest}
                        onCommentsChanged={() => {
                            commentCount.refresh();
                            void qc.invalidateQueries({ queryKey: ["drive", "activity"] });
                        }}
                        onCommentCount={(itemId, open) => {
                            if (itemId === badgeItem?.id) commentCount.set(open);
                        }}
                    />
                ) : null}
            </div>

            {menu ? (
                <Menu
                    label={menu.targets.length ? "Item actions" : "Create"}
                    anchor={menu.anchor}
                    focusFirst={menu.keyboard}
                    onClose={() => setMenu(null)}
                    entries={
                        menu.targets.length
                            ? buildItemMenu(menu.targets, {
                                  variant,
                                  actions,
                                  list: visible,
                                  onDetails: openDetails,
                                  onComment: openComments,
                                  zipParentId: zipParentFor(menu.targets)
                              })
                            : buildCreateMenu(parentId ?? null, actions, uploads)
                    }
                />
            ) : null}

            {dragging ? (
                <div className="pointer-events-none absolute inset-2 z-20 flex items-end justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 pb-10">
                    <div className="drive-slide-up flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-lg">
                        <Upload className="h-4 w-4" /> Drop to upload to {currentFolder?.name ?? "My Drive"}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function PageTitle({ icon: Icon, iconClass, children }: { icon: LucideIcon; iconClass?: string; children: ReactNode }) {
    return (
        <h1 className="flex min-w-0 items-center gap-2 text-base font-semibold leading-6 sm:text-[18px]">
            <Icon className={cn("h-5 w-5 shrink-0", iconClass ?? "text-[var(--drive-text-2)]")} aria-hidden />
            <span className="truncate">{children}</span>
        </h1>
    );
}

function BrowserSkeleton({ view }: { view: ViewMode }) {
    if (view === "grid") {
        return (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-3 px-4 pt-3" aria-busy="true" aria-label="Loading">
                {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="overflow-hidden rounded-lg border border-[var(--drive-line)] bg-card">
                        <div className="flex h-11 items-center gap-2 px-3">
                            <div className="h-5 w-5 animate-pulse rounded bg-muted" />
                            <div className="h-3 flex-1 animate-pulse rounded bg-muted" />
                        </div>
                        <div className="px-2 pb-2">
                            <div className="aspect-[4/3] animate-pulse rounded-lg bg-muted" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div aria-busy="true" aria-label="Loading" className="pt-9">
            {Array.from({ length: 9 }, (_, i) => (
                <div key={i} className="flex h-10 items-center gap-3 border-b border-[var(--drive-line)] px-3">
                    <div className="h-6 w-6 animate-pulse rounded bg-muted" />
                    <div className="h-3 animate-pulse rounded bg-muted" style={{ width: `${30 + ((i * 17) % 40)}%` }} />
                    <div className="ml-auto hidden h-3 w-24 animate-pulse rounded bg-muted sm:block" />
                </div>
            ))}
        </div>
    );
}
